import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { FILE_BASE } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { XCircle, ImageSquare, CalendarBlank, ArrowClockwise } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));

const fmtIstDateTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const opts = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" };
        return new Intl.DateTimeFormat("en-GB", opts).format(d).replace(",", "");
    } catch { return iso; }
};

// IST "today" as YYYY-MM-DD for the date-input defaults.
const istToday = () => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
    const ist = new Date(utcMs + 5.5 * 60 * 60_000);
    const p = (n) => String(n).padStart(2, "0");
    return `${ist.getFullYear()}-${p(ist.getMonth() + 1)}-${p(ist.getDate())}`;
};
const istYesterday = () => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
    const ist = new Date(utcMs + 5.5 * 60 * 60_000 - 24 * 60 * 60_000);
    const p = (n) => String(n).padStart(2, "0");
    return `${ist.getFullYear()}-${p(ist.getMonth() + 1)}-${p(ist.getDate())}`;
};
const istDaysAgo = (n) => {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
    const ist = new Date(utcMs + 5.5 * 60 * 60_000 - n * 24 * 60 * 60_000);
    const p = (x) => String(x).padStart(2, "0");
    return `${ist.getFullYear()}-${p(ist.getMonth() + 1)}-${p(ist.getDate())}`;
};

/**
 * v1.27.69 — Rejections tab (rendered inside CustomerReports).
 * v1.27.70 — Now supports a chosen date range instead of only y+today.
 *
 * Backend contract: GET /api/customer/rejections?date_from=YYYY-MM-DD&date_to=YYYY-MM-DD
 * (falls back to ?days=N when neither is provided). Party-scoped.
 */
export default function CustomerRejections() {
    const [dateFrom, setDateFrom] = useState(istYesterday());
    const [dateTo, setDateTo] = useState(istToday());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (df, dt) => {
        setLoading(true);
        try {
            const params = {};
            if (df) params.date_from = df;
            if (dt) params.date_to = dt;
            const r = await api.get("/customer/rejections", { params });
            setData(r.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(dateFrom, dateTo); }, [dateFrom, dateTo, load]);

    const applyPreset = (kind) => {
        const today = istToday();
        if (kind === "today")      { setDateFrom(today); setDateTo(today); return; }
        if (kind === "yday_today") { setDateFrom(istYesterday()); setDateTo(today); return; }
        if (kind === "last7")      { setDateFrom(istDaysAgo(6)); setDateTo(today); return; }
        if (kind === "last30")     { setDateFrom(istDaysAgo(29)); setDateTo(today); return; }
        if (kind === "this_month") {
            const now = new Date();
            const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
            const ist = new Date(utcMs + 5.5 * 60 * 60_000);
            const p = (x) => String(x).padStart(2, "0");
            setDateFrom(`${ist.getFullYear()}-${p(ist.getMonth() + 1)}-01`);
            setDateTo(today);
        }
    };

    const rows = data?.rows || [];
    const rangeLabel = useMemo(() => {
        if (!data) return "";
        return `${data.date_from} → ${data.date_to}`;
    }, [data]);

    return (
        <div className="space-y-4" data-testid="customer-rejections-page">
            {/* Range picker */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="rejections-range-picker">
                <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    <CalendarBlank size={14} weight="duotone" />
                    Choose date range
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">From</Label>
                        <Input
                            data-testid="rejections-date-from"
                            type="date"
                            value={dateFrom}
                            max={dateTo || undefined}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">To</Label>
                        <Input
                            data-testid="rejections-date-to"
                            type="date"
                            value={dateTo}
                            min={dateFrom || undefined}
                            max={istToday()}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="mt-1"
                        />
                    </div>
                    <div className="flex items-end">
                        <Button
                            data-testid="rejections-refresh"
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={() => load(dateFrom, dateTo)}
                            disabled={loading}
                        >
                            <ArrowClockwise size={14} className="mr-2" />
                            {loading ? "Refreshing…" : "Refresh"}
                        </Button>
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]" data-testid="rejections-presets">
                    <PresetChip label="Today"        onClick={() => applyPreset("today")}      testId="preset-today" />
                    <PresetChip label="Yday + Today" onClick={() => applyPreset("yday_today")} testId="preset-yday-today" />
                    <PresetChip label="Last 7 days"  onClick={() => applyPreset("last7")}      testId="preset-last7" />
                    <PresetChip label="This month"   onClick={() => applyPreset("this_month")} testId="preset-this-month" />
                    <PresetChip label="Last 30 days" onClick={() => applyPreset("last30")}     testId="preset-last30" />
                </div>
            </div>

            {/* Summary strip */}
            <div className="flex flex-wrap items-end justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-rose-700">Rejected slips</div>
                    <div className="mt-0.5 text-sm text-rose-900" data-testid="rejections-range-label">
                        Period: <span className="font-semibold">{rangeLabel || "…"}</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-display text-2xl font-bold tabular-nums text-rose-800" data-testid="rejections-total-count">
                        {data?.count ?? 0} slip{(data?.count ?? 0) === 1 ? "" : "s"}
                    </div>
                    <div className="text-xs font-semibold tabular-nums text-rose-700" data-testid="rejections-total-amount">
                        {fmt(data?.amount || 0)}
                    </div>
                </div>
            </div>

            {loading && !data ? (
                <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="rejections-loading">
                    Loading rejections…
                </div>
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={XCircle}
                    title="No rejections in this range"
                    description="No slips were rejected in the selected date range. Try widening the range or pick a preset above."
                />
            ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {rows.map((u) => (
                        <div key={u.id} data-testid={`rejection-card-${u.id}`} className="overflow-hidden rounded-xl border border-rose-200 bg-white shadow-sm">
                            <div className="flex gap-3 p-3">
                                {u.file_url ? (
                                    <a href={`${FILE_BASE}${u.file_url}`} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`rejection-slip-link-${u.id}`}>
                                        <img
                                            src={`${FILE_BASE}${u.file_url}`}
                                            alt="slip"
                                            className="h-20 w-20 rounded-md bg-slate-100 object-cover ring-1 ring-slate-200"
                                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                                        />
                                    </a>
                                ) : (
                                    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400 ring-1 ring-slate-200">
                                        <ImageSquare size={26} weight="duotone" />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-mono text-xs text-slate-600 truncate" data-testid={`rejection-utr-${u.id}`}>
                                                UTR: <span className="text-slate-800">{u.utr || "—"}</span>
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-slate-500">
                                                Uploaded: <span className="text-slate-700">{fmtIstDateTime(u.uploaded_at)}</span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-display text-lg font-bold tabular-nums text-slate-900" data-testid={`rejection-amount-${u.id}`}>{fmt(u.amount)}</div>
                                            <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800">
                                                <XCircle size={11} weight="fill" className="mr-1" /> REJECTED
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="border-t border-rose-100 bg-rose-50/40 px-3 py-2">
                                <div className="text-[10px] font-semibold uppercase tracking-widest text-rose-700">Admin remark</div>
                                <div className="mt-1 text-sm text-slate-800" data-testid={`rejection-remark-${u.id}`}>
                                    {u.verification_notes && u.verification_notes.trim()
                                        ? u.verification_notes
                                        : <span className="italic text-slate-400">No remark provided.</span>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function PresetChip({ label, onClick, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 font-semibold text-slate-600 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-800"
        >
            {label}
        </button>
    );
}
